import { useState } from 'react';
import { Share2, Link2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const TwitterIcon = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const LinkedInIcon = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const WhatsAppIcon = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const shareTargets = [
  {
    key: 'copy',
    label: 'Copy Link',
    icon: Link2,
    color: 'bg-stone-100 hover:bg-stone-200 text-stone-700',
  },
  {
    key: 'twitter',
    label: 'X / Twitter',
    icon: TwitterIcon,
    color: 'bg-stone-900 hover:bg-black text-white',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: FacebookIcon,
    color: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    icon: LinkedInIcon,
    color: 'bg-sky-700 hover:bg-sky-800 text-white',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: WhatsAppIcon,
    color: 'bg-green-500 hover:bg-green-600 text-white',
  },
];

export default function ShareButton({ title, url, description }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareText = `Check out "${title}" on PushPro`;

  const handleShare = async (target) => {
    switch (target) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error('Failed to copy link');
        }
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400'
        );
        setOpen(false);
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400'
        );
        setOpen(false);
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400'
        );
        setOpen(false);
        break;
      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
          '_blank'
        );
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative">
      <Button
        data-testid="share-product-btn"
        variant="outline"
        onClick={() => setOpen(!open)}
        className="rounded-full h-10 px-5 border-border/60 hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-all duration-300 text-sm gap-2"
      >
        <Share2 size={16} />
        Share
      </Button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Dropdown */}
          <div
            className="absolute right-0 top-12 z-50 bg-white rounded-2xl border border-border/60 shadow-2xl shadow-black/10 p-2 w-56 animate-in fade-in slide-in-from-top-2 duration-200"
            data-testid="share-dropdown"
          >
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 pt-2 pb-1 font-medium">Share to</p>
            {shareTargets.map((t) => {
              const Icon = t.icon;
              const isCopy = t.key === 'copy';
              return (
                <button
                  key={t.key}
                  onClick={() => handleShare(t.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${t.color}`}
                  data-testid={`share-${t.key}`}
                >
                  {isCopy && copied ? <Check size={16} /> : <Icon size={16} />}
                  {isCopy && copied ? 'Copied!' : t.label}
                </button>
              );
            })}
            <button
              onClick={() => setOpen(false)}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 mt-1 rounded-xl text-xs text-muted-foreground hover:bg-stone-50 transition-colors"
            >
              <X size={12} /> Close
            </button>
          </div>
        </>
      )}
    </div>
  );
}
